// HomePage — Dashboard con datos reales de la empresa activa.
// Greeting + KPIs operativos del día + últimos 8 listados + accesos rápidos.

import React, { useMemo, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Spin, Alert } from 'antd';
import {
    FileOutlined,
    SettingOutlined,
    KeyOutlined,
    DeleteRowOutlined,
    ToolOutlined,
    PlusOutlined,
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';
import { dashboardService } from '../services/api';

const greetingFor = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Buenos días';
    if (h < 19) return 'Buenas tardes';
    return 'Buenas noches';
};

const formatDate = () => {
    try {
        const d = new Date();
        const txt = d.toLocaleDateString('es-MX', {
            weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'
        });
        return txt.charAt(0).toUpperCase() + txt.slice(1);
    } catch (e) {
        return '';
    }
};

// Tiempo relativo simple en español, sin dependencias externas.
const relativeTime = (dateInput) => {
    if (!dateInput) return '';
    const then = new Date(dateInput).getTime();
    if (isNaN(then)) return '';
    const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
    if (diffSec < 60) return 'hace unos segundos';
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `hace ${diffMin} min`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `hace ${diffH} h`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `hace ${diffD} d${diffD === 1 ? 'ía' : 'ías'}`;
    return new Date(dateInput).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
};

const toneForList = (list) => {
    if (list.deletionStatus === 'pendiente_eliminacion') return 'accent';
    if (list.status === 'completed' || list.status === 'completo') return 'success';
    return 'primary';
};

const KPI_LABELS = {
    deliveriesOpen:               'Entregas abiertas',
    deliveriesWithoutPackingList: 'Sin listado',
    packingListsToday:            'Listados hoy',
    palletsToday:                 'Tarimas hoy',
};

const HomePage = () => {
    const { user, pendingCount } = useAuth();
    const { brand } = useBrand();
    const navigate = useNavigate();
    const permissions = user?.role?.profile?.permissions?.pages;

    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const firstName = useMemo(() => {
        if (!user) return '';
        if (user.fullName) return user.fullName.split(' ')[0];
        return user.username || '';
    }, [user]);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setError(null);
        dashboardService.getSummary()
            .then(resp => {
                if (!alive) return;
                if (resp.data?.success) setSummary(resp.data.data);
                else setError(resp.data?.message || 'No se pudo cargar el resumen.');
            })
            .catch(err => {
                if (!alive) return;
                setError(err.response?.data?.message || err.message || 'Error de red al cargar el dashboard.');
            })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    const quickAccess = useMemo(() => {
        const items = [];
        if (permissions?.viewDeliveries) items.push({
            key: 'deliveries', label: 'Entregas', sub: 'Buscar y empacar',
            icon: <FileOutlined />, path: '/deliveries',
        });
        if (permissions?.viewDeletionManagement) items.push({
            key: 'deletion', label: 'Gestión solicitudes',
            sub: pendingCount > 0 ? `${pendingCount} pendiente${pendingCount === 1 ? '' : 's'}` : 'Sin pendientes',
            icon: <DeleteRowOutlined />, path: '/solicitudes-eliminacion', badge: pendingCount,
        });
        items.push({
            key: 'account', label: 'Mi cuenta', sub: 'Cambiar contraseña',
            icon: <KeyOutlined />, path: '/my-account',
        });
        if (permissions?.viewAdminUsers) items.push({
            key: 'users', label: 'Gestión de acceso', sub: 'Usuarios y permisos',
            icon: <SettingOutlined />, path: '/administracion/usuarios',
        });
        if (permissions?.viewAdminConfig) items.push({
            key: 'settings', label: 'Configuración', sub: 'Sistema y catálogos',
            icon: <ToolOutlined />, path: '/administracion/configuracion',
        });
        return items;
    }, [permissions, pendingCount]);

    const dotColors = {
        primary: brand.primary,
        success: '#1f8a5a',
        accent: brand.accent,
        muted: '#5e6779',
    };

    const kpis = summary?.kpis || {};
    const recentLists = summary?.recentPackingLists || [];

    const formatKpi = (n) => (typeof n === 'number' ? n.toLocaleString('es-MX') : '—');

    return (
        <div className="dashboard">
            {/* Greeting */}
            <div className="dashboard-greet">
                <div>
                    <h1 className="dashboard-greet-title">
                        {greetingFor()}, {firstName}
                    </h1>
                    <div className="dashboard-greet-sub">
                        {formatDate()}
                    </div>
                </div>
                {permissions?.viewDeliveries && (
                    <button
                        className="dashboard-cta"
                        onClick={() => navigate('/deliveries')}
                    >
                        <PlusOutlined /> Ir a entregas
                    </button>
                )}
            </div>

            {error && (
                <Alert
                    type="warning"
                    message="No se pudo cargar el dashboard"
                    description={error}
                    style={{ marginBottom: 16 }}
                    showIcon
                />
            )}

            {/* KPIs */}
            <div className="kpi-grid">
                {Object.keys(KPI_LABELS).map(key => (
                    <div key={key} className="kpi-card">
                        <div className="kpi-label">{KPI_LABELS[key]}</div>
                        <div className="kpi-value-row">
                            <span className="kpi-value">
                                {loading ? <Spin size="small" /> : formatKpi(kpis[key])}
                            </span>
                        </div>
                    </div>
                ))}
            </div>

            {/* Two columns */}
            <div className="dashboard-grid">
                <div className="dashboard-card">
                    <div className="dashboard-card-head">
                        <div className="dashboard-card-title">Listados recientes</div>
                        <div className="dashboard-card-meta">
                            {loading ? 'cargando…' : `últimos ${recentLists.length}`}
                        </div>
                    </div>
                    {!loading && recentLists.length === 0 && (
                        <div className="activity-row last">
                            <div style={{ flex: 1, color: 'var(--text-muted, #5e6779)' }}>
                                Aún no hay listados recientes en esta empresa.
                            </div>
                        </div>
                    )}
                    {recentLists.map((l, i) => {
                        const tone = toneForList(l);
                        return (
                            <div
                                key={l.id}
                                className={`activity-row ${i === recentLists.length - 1 ? 'last' : ''}`}
                                role="button"
                                tabIndex={0}
                                onClick={() => l.docEntry && navigate(`/packing-list/${l.docEntry}`)}
                                onKeyDown={(e) => {
                                    if ((e.key === 'Enter' || e.key === ' ') && l.docEntry) {
                                        e.preventDefault();
                                        navigate(`/packing-list/${l.docEntry}`);
                                    }
                                }}
                                style={{ cursor: l.docEntry ? 'pointer' : 'default' }}
                            >
                                <span className="activity-dot" style={{ background: dotColors[tone] }} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div className="activity-title">
                                        {l.packingListNumber || '(sin folio)'}
                                        {l.deletionStatus === 'pendiente_eliminacion' && (
                                            <span style={{ marginLeft: 8, fontSize: 11, color: brand.accent }}>
                                                · solicitud de eliminación
                                            </span>
                                        )}
                                    </div>
                                    <div className="activity-sub">
                                        {(l.cardName || l.cardCode || 'Cliente N/D')} · {relativeTime(l.updatedAt)}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="dashboard-card">
                    <div className="dashboard-card-title" style={{ marginBottom: 12 }}>
                        Accesos rápidos
                    </div>
                    {quickAccess.map((q, i) => (
                        <Link
                            key={q.key}
                            to={q.path}
                            className={`quick-row ${i === quickAccess.length - 1 ? 'last' : ''}`}
                        >
                            <span className="quick-icon">{q.icon}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div className="quick-label">{q.label}</div>
                                <div className="quick-sub">{q.sub}</div>
                            </div>
                            {q.badge > 0 && (
                                <span className="quick-badge">{q.badge}</span>
                            )}
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default HomePage;
